import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import MFASettings from "./MFASettings";
import PasswordSettings from "./PasswordSettings";
import SessionManager from "./SessionManager";
import AuditLog from "./AuditLog";

const SecuritySettings = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("password");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const sections = [
    {
      id: "password",
      name: "Password",
      icon: "🔑",
      description: "Change your password",
    },
    {
      id: "mfa",
      name: "Multi-Factor Authentication",
      icon: "🛡️",
      description: "Secure your account with 2FA",
    },
    {
      id: "sessions",
      name: "Active Sessions",
      icon: "📱",
      description: "Manage your active sessions",
    },
    {
      id: "audit",
      name: "Security Log",
      icon: "📋",
      description: "View security events",
    },
  ];

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleMessage = (msg) => {
    setMessage(msg);
  };

  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "password":
        return (
          <PasswordSettings
            onMessage={handleMessage}
            onLoading={handleLoading}
          />
        );
      case "mfa":
        return (
          <MFASettings onMessage={handleMessage} onLoading={handleLoading} />
        );
      case "sessions":
        return (
          <SessionManager onMessage={handleMessage} onLoading={handleLoading} />
        );
      case "audit":
        return <AuditLog onMessage={handleMessage} onLoading={handleLoading} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Security Settings
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage your account security preferences and monitor security events
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : message.type === "warning"
                ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === "success" && (
                <span className="text-green-500">✓</span>
              )}
              {message.type === "warning" && (
                <span className="text-yellow-500">⚠</span>
              )}
              {message.type === "error" && (
                <span className="text-red-500">✗</span>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{message.text}</p>
              {message.details && (
                <p className="mt-1 text-xs">{message.details}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600 text-center">Processing...</p>
          </div>
        </div>
      )}

      {/* Security Sections */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Section Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === section.id
                    ? "border-primary-500 text-primary-600 bg-primary-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-base">{section.icon}</span>
                  <div className="text-left">
                    <div className="font-medium">{section.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                      {section.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Section Content */}
        <div className="p-6">{renderSectionContent()}</div>
      </div>

      {/* Security Tips */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          Security Tips
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use a strong, unique password for your account</li>
          <li>• Enable multi-factor authentication for enhanced security</li>
          <li>• Regularly review your active sessions and audit log</li>
          <li>• Sign out of unused sessions, especially on shared devices</li>
        </ul>
      </div>
    </div>
  );
};

export default SecuritySettings;
